class ProductConfigurator extends HTMLElement {
    constructor() {
        super();
        this.init();

    }

    init() {
        this.allowPartial = this.dataset.allowPartial === 'true';
        try {
            this.productData = JSON.parse(this.querySelector('script[type="application/json"]:not([data-selected-variant])').textContent);
            console.log("Product data loaded:", this.productData);
            this.updateExternalProductUrls();
            this.sortNatural();
        } catch (e) {
            console.error("Error parsing product data:", e);
        }
    }

    sortNatural() {
        const fieldsets = this.querySelectorAll('fieldset');
        fieldsets.forEach(fieldset => {
            const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

            inputs.sort((a, b) => {
                return this.naturalCompare(a.value, b.value);
            });

            // Clear the fieldset and reappend the sorted inputs and their corresponding labels
            inputs.forEach(input => {
                const label = fieldset.querySelector(`label[for="${input.id}"]`);
                if (label) {
                    fieldset.appendChild(input);
                    fieldset.appendChild(label);
                }
            });

            console.log("Sorted inputs and labels for fieldset:", fieldset);
        });
    }

    naturalCompare(a, b) {
        const extractParts = str => str.match(/(\d+|\D+)/g).map(part => isNaN(part) ? part : parseInt(part, 10));
        const partsA = extractParts(a);
        const partsB = extractParts(b);

        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
            const partA = partsA[i] || '';
            const partB = partsB[i] || '';

            if (partA !== partB) {
                if (typeof partA === 'number' && typeof partB === 'number') {
                    return partA - partB;
                }
                return partA > partB ? 1 : -1;
            }
        }
        return 0;
    }

    updateExternalProductUrls() {
        const currentUrl = window.location.pathname; // Get current URL path
        const selectedOptions = this.getSelectedOptions();
        
        // Flag to track if we found an exact match for the third set
        let exactMatchFoundForThirdSet = false;

        this.querySelectorAll('input[data-external-product="true"]').forEach(input => {
            const optionName = input.getAttribute('data-option');
            const value = input.value.trim();
            // Temporarily override the option for this specific input
            const testOptions = { ...selectedOptions, [optionName]: value };
            console.log("Testing options:", testOptions);
            
            let product = this.findProduct(testOptions);

            if (this.isThirdSetOption(input)) {
                // Handle third set specifically
                if (product && this.isExactMatch(testOptions, product)) {
                    input.disabled = false;
                    input.setAttribute('data-product-url', product.url);
                    console.log(`Set product URL for option ${input.value}: ${product.url}`);
                    exactMatchFoundForThirdSet = true; // Mark as exact match found for third set
                } else {
                    input.disabled = true; // Disable if no exact match for third set
                    console.warn(`No exact match found for third set option: ${input.value}`);
                }
            } else {
                // Handle other sets
                if (!product) {
                    product = this.findClosestProduct(testOptions, currentUrl);
                }

                if (product) {
                    if (product.url === currentUrl) {
                        input.disabled = true;
                        console.log(`Product URL matches current URL. Input disabled for option ${input.value}.`);
                    } else {
                        input.setAttribute('data-product-url', product.url);
                        console.log(`Set product URL for option ${input.value}: ${product.url}`);
                    }
                } else {
                    input.disabled = true;
                    console.warn(`No matching product found for options: ${JSON.stringify(testOptions)}`);
                }
            }
        });

        // If no exact match found for the third set, ensure all third set options are disabled
        if (!exactMatchFoundForThirdSet) {
            this.disableThirdSetOptions();
        }
    }

    getSelectedOptions() {
        const selectedOptions = {};
        this.querySelectorAll('input[type="radio"]:checked').forEach(input => {
            const optionName = input.getAttribute('data-option');
            const value = input.value.trim();
            if (optionName) {
                selectedOptions[optionName] = value;
            }
        });
        return selectedOptions;
    }

    findProduct(selectedOptions) {
        console.log("Finding product for options:", selectedOptions);
        for (const product of this.productData) {
            console.log("Checking product:", product);
            for (const option of product.options) {
                console.log("Comparing options:", option, selectedOptions);
                const matches = Object.keys(selectedOptions).every(key => {
                    const optionValue = option[key];
                    const selectedValue = selectedOptions[key];
                    const isMatch = optionValue === selectedValue;
                    console.log(`Matching - Key: "${key}", Option Value: "${optionValue}", Selected Value: "${selectedValue}", Match: ${isMatch}`);
                    return isMatch;
                });
                if (matches) {
                    console.log("Exact match found:", product);
                    return product;
                }
            }
        }
        console.warn("No exact match found for options:", selectedOptions);
        return null;
    }

    findClosestProduct(selectedOptions, currentUrl) {
        console.log("Finding closest product for options:", selectedOptions);

        // Get the first two keys from the selected options
        const importantKeys = Object.keys(selectedOptions).slice(0, 2);
        console.log("Important keys (based on order):", importantKeys);

        let partialMatch;

        // First attempt: Relax all constraints except the important keys
        for (const product of this.productData) {
            if (product.url === currentUrl) {
                continue; // Skip if the product URL matches the current URL
            }

            const found = product.options.some(option => {
                return importantKeys.every(key => {
                    if (selectedOptions[key]) {
                        const optionValue = option[key];
                        const selectedValue = selectedOptions[key];
                        const isMatch = optionValue === selectedValue;
                        console.log(`Priority Matching - Key: "${key}", Option Value: "${optionValue}", Selected Value: "${selectedValue}", Match: ${isMatch}`);
                        return isMatch;
                    }
                    return false;
                });
            });

            if (found) {
                partialMatch = product;
                break;
            }
        }

        if (partialMatch) {
            return partialMatch;
        }

        // Second attempt: If no match is found, relax the constraints on one of the important keys
        console.log(this.allowPartial);
        if (this.allowPartial) {
            
            for (const product of this.productData) {
                if (product.url === currentUrl) {
                    continue; // Skip if the product URL matches the current URL
                }

                const found = product.options.some(option => {
                    return importantKeys.some(key => {
                        const optionValue = option[key];
                        const selectedValue = selectedOptions[key];
                        const isMatch = optionValue === selectedValue;
                        console.log(`Secondary Matching - Key: "${key}", Option Value: "${optionValue}", Selected Value: "${selectedValue}", Match: ${isMatch}`);
                        return isMatch;
                    });
                });

                if (found) {
                    partialMatch = product;
                    break;
                }
            }

            return partialMatch;
        }

    }

    isThirdSetOption(input) {
        // Determine if the input is part of the third set
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        return fieldsets[2] && fieldsets[2].contains(input);
    }

    disableThirdSetOptions() {
        // Disable all options in the third set
        const thirdFieldset = this.querySelectorAll('fieldset')[2];
        if (thirdFieldset) {
            thirdFieldset.querySelectorAll('input[type="radio"]').forEach(input => {
                input.disabled = true;
            });
        }
    }

    isExactMatch(selectedOptions, product) {
        // Check if the selected options match exactly with the product's options
        return product.options.some(option => {
            return Object.keys(selectedOptions).every(key => {
                return option[key] === selectedOptions[key];
            });
        });
    }
}

customElements.define('product-configurator', ProductConfigurator);